import React, { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { Input } from '@/app/components/form/Input';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { CreateClientFormData, EditClientResponse, LoyaltyNumbersCategory } from '@/types/clientsApiTypes';
import type { Nullable } from '@/types/common';
import PlusIcon from '/public/icons/plus.svg';
import TrashIcon from '/public/icons/trash.svg';

interface LoyaltyProgramProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => Promise<EditClientResponse | undefined>;
  onDeleteLoyaltyProgram?: (lpId: number) => Promise<void> | void;
  fieldPrefix?: 'primary_passenger' | `related_passengers.${number}`;
}

export const LoyaltyProgram = ({
  onUpdateFormData,
  onDeleteLoyaltyProgram,
  fieldPrefix = 'primary_passenger',
}: LoyaltyProgramProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CreateClientFormData>();

  const [isDeletingLoyaltyProgram, setIsDeletingLoyaltyProgram] = React.useState(false);

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: `${fieldPrefix}.loyalty_numbers`,
  });

  const handleAddFrequentFlyerProgram = () => {
    append(
      {
        lp_id: null,
        category_id: null,
        number: '',
        loyalty_program: '',
        frequent_flyer_number: '',
        is_legacy: false,
        note: '',
      },
      { shouldFocus: false }
    );
  };

  const mapOptions = useCallback(
    (options: LoyaltyNumbersCategory[]): SelectorOption<number>[] =>
      options.map((option) => ({
        value: option.id,
        label: option.title,
        frequent_flyer_number: option.frequent_flyer_number,
        note: '',
      })),
    []
  );

  const onFrequentFlyerProgramSelect = async (option: SelectorOption<string | number> | null, fieldIndex: number) => {
    if (!option) return;

    if (!onUpdateFormData) {
      update(fieldIndex, {
        lp_id: null,
        category_id: Number(option.value),
        is_legacy: false,
        number: '',
        loyalty_program: option.label,
        frequent_flyer_number: option.frequent_flyer_number as Nullable<string>,
        note: '',
      });
      return;
    }

    const existingLpId = fields[fieldIndex].lp_id;

    if (existingLpId) {
      update(fieldIndex, {
        lp_id: fields[fieldIndex].lp_id,
        category_id: Number(option.value),
        is_legacy: false,
        number: '',
        loyalty_program: option.label,
        frequent_flyer_number: option.frequent_flyer_number as Nullable<string>,
        note: '',
      });

      const updateExistingPayload = [
        {
          id: fields[fieldIndex].lp_id,
          loyalty_category_id: Number(option.value),
          number: '',
          loyalty_program: option.label,
          frequent_flyer_number: option.frequent_flyer_number as Nullable<string>,
          note: '',
        },
      ];
      onUpdateFormData?.(`${fieldPrefix}.loyalty_numbers`, updateExistingPayload);
      return;
    }

    const createNewPayload = [
      {
        category_id: Number(option.value),
        number: '',
        loyalty_program: option.label,
        frequent_flyer_number: option.frequent_flyer_number,
        note: '',
      },
    ];

    const response = await onUpdateFormData?.(`${fieldPrefix}.loyalty_numbers`, createNewPayload);
    if (response) {
      update(fieldIndex, {
        lp_id: response?.data?.loyalty_numbers.at(-1)?.id ?? null,
        category_id: Number(option.value),
        is_legacy: false,
        number: response?.data?.loyalty_numbers.at(-1)?.number ?? '',
        loyalty_program: response?.data?.loyalty_numbers.at(-1)?.loyalty_program ?? '',
        frequent_flyer_number: option.frequent_flyer_number as Nullable<string>,
        note: response?.data?.loyalty_numbers.at(-1)?.note ?? '',
      });
    }
  };

  const onUpdateFrequentFlyerNumber = (value: string, fieldIndex: number) => {
    update(fieldIndex, {
      lp_id: fields[fieldIndex].lp_id,
      is_legacy: fields[fieldIndex].is_legacy,
      category_id: fields[fieldIndex].category_id,
      loyalty_program: fields[fieldIndex].loyalty_program,
      frequent_flyer_number: fields[fieldIndex].frequent_flyer_number,
      note: fields[fieldIndex].note,
      number: value,
    });

    if (onUpdateFormData) {
      const updatePayload = [
        {
          id: fields[fieldIndex].lp_id,
          loyalty_category_id: fields[fieldIndex].category_id,
          number: value,
          loyalty_program: fields[fieldIndex].loyalty_program,
          frequent_flyer_number: fields[fieldIndex].frequent_flyer_number as Nullable<string>,
          note: fields[fieldIndex].note,
        },
      ];

      onUpdateFormData?.(`${fieldPrefix}.loyalty_numbers`, updatePayload);
    }
  };

  const onDeleteLoyaltyProgramHandler = async (fieldIndex: number) => {
    const field = fields[fieldIndex];

    if (!field.lp_id || !onDeleteLoyaltyProgram) {
      remove(fieldIndex);
      return;
    }

    setIsDeletingLoyaltyProgram(true);
    try {
      await onDeleteLoyaltyProgram(field.lp_id);
      toast.success('Loyalty program deleted successfully');
      remove(fieldIndex);
    } catch {
      toast.error('Failed to delete loyalty program');
    } finally {
      setIsDeletingLoyaltyProgram(false);
    }
  };

  return (
    <div className="flex flex-col items-start justify-start mt-4">
      <div className="flex flex-col gap-3 w-full">
        {fields.map((field, fieldIndex) => (
          <div className="flex gap-[20px] items-end" key={field.id}>
            {!field.category_id && field.loyalty_program ? (
              <div className="w-[50%]">
                <Input
                  className="w-full"
                  autoComplete="off"
                  value={`${field.loyalty_program} (legacy)`}
                  intent="primaryWhite"
                  label="Loyalty program"
                  name={`${fieldPrefix}.loyalty_numbers.${fieldIndex}.loyalty_program` as const}
                  errors={errors}
                  disabled
                  readOnly
                />
              </div>
            ) : (
              <InfinityAsyncSelector<LoyaltyNumbersCategory>
                placeholder="Select loyalty program"
                url="/api/loyalty-numbers/categories"
                queryKey={['loyalty-numbers-categories']}
                mapOptions={mapOptions}
                className="!w-[50%]"
                isClearOption={false}
                label="Loyalty program"
                isClearable={false}
                value={field.category_id ?? ''}
                onChange={(option) => onFrequentFlyerProgramSelect(option, fieldIndex)}
              />
            )}

            <div className="w-[50%]">
              <Input
                className="w-full"
                autoComplete="off"
                placeholder="Enter number"
                {...register(`${fieldPrefix}.loyalty_numbers.${fieldIndex}.number`)}
                intent="primaryWhite"
                label="Number"
                errors={errors}
                onBlur={(e) => onUpdateFrequentFlyerNumber(e.target.value, fieldIndex)}
              />
            </div>
            <IconButton
              type="button"
              disabled={isDeletingLoyaltyProgram}
              icon={<TrashIcon className="w-4 h-4" />}
              onClick={() => onDeleteLoyaltyProgramHandler(fieldIndex)}
              color="danger"
            />
          </div>
        ))}
      </div>

      <BaseButton
        onClick={handleAddFrequentFlyerProgram}
        type="button"
        size="ghost_small"
        startIcon={<PlusIcon />}
        intent="ghost"
        className="w-fit mt-3"
      >
        Add New Loyalty Program
      </BaseButton>
    </div>
  );
};